import { Injectable, signal } from "@angular/core";
import { LessonInvoiceAddRequest } from "src/app/office/interfaces/lesson_invoice_add_request";
import {
  AlertController,
  ModalController,
  NavController,
  Platform,
  ToastController,
  isPlatform,
} from "@ionic/angular";
import { NewInvoiceDialogComponent } from "src/app/office/components/new-invoice-dialog/new-invoice-dialog.component";
import { NewLessonDialogComponent } from "src/app/lessons/components/new-lesson-dialog/new-lesson-dialog.component";
import { NewStudentDialogComponent } from "src/app/students/components/new-student-dialog/new-student-dialog.component";
import { NewPaymentDialogComponent } from "src/app/office/components/new-payment-dialog/new-payment-dialog.component";
import { InvoiceDetailPageComponent } from "src/app/office/containers/invoice-detail-page/invoice-detail-page.component";
import { EducationFormComponent } from "src/app/settings/components/education/education-form/education-form.component";
import { EducationTitleFormComponent } from "src/app/settings/components/education/education-title-form/education-title-form.component";
import { EducationCardComponent } from "src/app/lessons/components/education-card/education-card.component";
import { EducationCategoryDto } from "src/app/settings/interfaces/education_category_dto";
import { StudentDto } from "src/app/students/interfaces/student_dto";
import { EducationBodyFormComponent } from "src/app/settings/components/education/education-body-form/education-body-form.component";
import { AppointmentDto } from "src/app/calendar/interfaces/appointment_dto";
import { ExamDialogComponent } from "src/app/lessons/components/exam-dialog/exam-dialog.component";
import { EducationTitleDto } from "src/app/settings/interfaces/education_title_dto";
import { EducationBodyDto } from "src/app/settings/interfaces/education_body_dto";
import { MapsComponent } from "src/app/tracking/maps/maps.component";
import { CommentDialogComponent } from "src/app/tracking/comment-dialog/comment-dialog.component";
import { LocationTrackingDto } from "src/app/tracking/interfaces/location_tracking";
import { MileageDialogComponent } from "src/app/lessons/components/mileage-dialog/mileage-dialog.component";
import { LessonDetailComponent } from "src/app/lessons/containers/lesson-detail-page/lesson-detail.component";
import { ExamListComponent } from "src/app/students/components/exam-list/exam-list.component";
import { ReportGeneratorDto } from "src/app/report-generation/interfaces/report_generator_dto";
import { ReportGenerationComponent } from "src/app/report-generation/components/report-generation/report-generation.component";
import { ServiceDialogComponent } from "src/app/settings/components/service-dialog/service-dialog.component";
import { ServiceDto } from "src/app/settings/interfaces/service_dto";
import { SubscriptionDto } from "src/app/settings/interfaces/subscription_dto";
import { SubscriptionDialogComponent } from "src/app/settings/components/subscription-dialog/subscription-dialog.component";
import { InvoiceItemDto } from "src/app/office/interfaces/invoice_item_dto";
import { Plans } from "../models/plans.enum";
import { Store } from "@ngrx/store";
import { selectEntitlements } from "src/app/auth/ngrx/customer.selectors";
import { ProductFormComponent } from "src/app/products/components/product-form/product-form.component";
import { ProductDto } from "src/app/products/interfaces/product_dto";
import { PaymentDetailPageComponent } from "src/app/office/containers/payment-detail-page/payment-detail-page.component";

@Injectable({
  providedIn: "root",
})
export class DialogService {
  constructor(
    private modalController: ModalController,
    private alertController: AlertController,
    private platform: Platform,
    private navController: NavController,
    private store: Store<any>,
    private toastCtrl: ToastController
  ) {}
  presentingElement = document.getElementById("router-outlet");
  desktop =
    isPlatform("desktop") ||
    isPlatform("phablet") ||
    isPlatform("ipad") ||
    (isPlatform("tablet") && !isPlatform("mobile") && !isPlatform("iphone"));

  openNewInvoiceDialog(invoice?: LessonInvoiceAddRequest) {
    this.modalController
      .create({
        component: NewInvoiceDialogComponent,
        componentProps: {
          invoice: signal(invoice ?? null),
        },
      })
      .then((modal) => modal.present());
  }

  openNewPaymentDialog(payment?: any) {
    this.modalController
      .create(
        this.desktop
          ? {
              component: NewPaymentDialogComponent,
              componentProps: {
                payment: signal(payment ?? null),
              },
            }
          : {
              component: NewPaymentDialogComponent,
              componentProps: {
                payment: signal(payment ?? null),
              },
              canDismiss: true,
              showBackdrop: true,
              backdropDismiss: true,
            }
      )
      .then((modal) => modal.present());
  }

  openLessonDetailDialog(lessonId?: number) {
    this.modalController
      .create({
        component: LessonDetailComponent,
        componentProps: {
          showCloseButton: signal(true),
          lessonId: signal(lessonId ?? null),
        },
      })
      .then((modal) => modal.present());
  }

  openNewLessonDialog(lesson?: any) {
    this.modalController
      .create({
        component: NewLessonDialogComponent,
        presentingElement:
          !this.desktop && !document.getElementsByClassName("modal-default")
            ? this.presentingElement
            : undefined,
        componentProps: {
          lesson: signal(lesson ?? null),
        },
      })
      .then((modal) => modal.present());
  }

  openNewStudentDialog(student?: StudentDto) {
    this.modalController
      .create({
        component: NewStudentDialogComponent,
        presentingElement:
          !this.desktop && !document.getElementsByClassName("modal-default")
            ? this.presentingElement
            : undefined,
        componentProps: {
          student: signal(student ?? null),
        },
      })
      .then((modal) => modal.present());
  }

  async openServiceDialog(service?: ServiceDto, tutorial: boolean = false) {
    await this.modalController
      .create({
        component: ServiceDialogComponent,
        presentingElement:
          !this.desktop && !document.getElementsByClassName("modal-default")
            ? this.presentingElement
            : undefined,
        componentProps: {
          service: signal(service ?? null),
          tutorial: signal(tutorial),
        },
      })
      .then((modal) => modal.present());
  }

  openSubscriptionDialog(subscription?: SubscriptionDto) {
    this.modalController
      .create({
        component: SubscriptionDialogComponent,
        presentingElement:
          !this.desktop && !document.getElementsByClassName("modal-default")
            ? this.presentingElement
            : undefined,
        componentProps: {
          subscription: signal(subscription ?? null),
        },
      })
      .then((modal) => modal.present());
  }

  openInvoiceDialog(invoice?: any) {
    this.modalController
      .create({
        component: InvoiceDetailPageComponent,
        presentingElement: !document.getElementsByClassName("modal-default")
          ? this.presentingElement
          : undefined,
        componentProps: {
          isModal: signal(true),
          invoice: signal(invoice ?? null),
        },
      })
      .then((modal) => modal.present());
  }

  openPaymentDialog(payment?: any) {
    this.modalController
      .create({
        component: PaymentDetailPageComponent,
        presentingElement: !document.getElementsByClassName("modal-default")
          ? this.presentingElement
          : undefined,
        componentProps: {
          isModal: signal(true),
          payment: signal(payment ?? null),
        },
      })
      .then((modal) => modal.present());
  }

  openEducationFormDialog(educationCategory: EducationCategoryDto = null, categoryOptions: string[]) {
    this.modalController
      .create({
        component: EducationFormComponent,
        presentingElement:
          !this.desktop && !document.getElementsByClassName("modal-default")
            ? this.presentingElement
            : undefined,
        componentProps: {
          educationCategory: signal(educationCategory),
          categoryOptions: signal(categoryOptions),
        },
      })
      .then((modal) => modal.present());
  }

  openEducationTitleFormDialog(
    educationCategory: number,
    rowOrder: number,
    educationTitle: EducationTitleDto = null
  ) {
    this.modalController
      .create({
        component: EducationTitleFormComponent,
        presentingElement:
          !this.desktop && !document.getElementsByClassName("modal-default")
            ? this.presentingElement
            : undefined,
        componentProps: {
          educationCategory: signal(educationCategory),
          rowOrder: signal(rowOrder),
          educationTitle: signal(educationTitle),
        },
      })
      .then((modal) => modal.present());
  }

  openMaps(appointment_id: number, duration: number) {
    const customerEntitlements = this.store.selectSignal(selectEntitlements);
    if (!(customerEntitlements()?.includes(Plans.premium) || customerEntitlements()?.includes(Plans.premium_promo))) {
      this.toastCtrl.create({
        message: 'Die Fahrtenaufzeichnung kann nur mit einem Premium-Abonnement genutzt werden.',
        duration: 3000,
        position: 'bottom'
      }).then(toast => toast.present());
      return;
    }
    this.modalController
      .create({
        component: MapsComponent,
        canDismiss: this.canDismiss,
        componentProps: {
          appointment_id: signal(appointment_id),
          duration: signal(duration),
        },
        cssClass:
          isPlatform("ipad") ||
          isPlatform("desktop") ||
          isPlatform("phablet") ||
          isPlatform("tablet")
            ? "fullscreen"
            : "",
      })
      .then((modal) => modal.present());
  }

  openEducationBodyFormDialog(
    educationTitle: number,
    rowOrder: number,
    educationBody: EducationBodyDto = null
  ) {
    this.modalController
      .create({
        component: EducationBodyFormComponent,
        presentingElement:
          !this.desktop && !document.getElementsByClassName("modal-default")
            ? this.presentingElement
            : undefined,
        componentProps: {
          educationTitle: signal(educationTitle),
          rowOrder: signal(rowOrder),
          educationBody: signal(educationBody),
        },
      })
      .then((modal) => modal.present());
  }

  openEducationCard(
    educationCategory: EducationCategoryDto,
    student: StudentDto,
    appointment: AppointmentDto,
    preventSwipeDismiss: boolean,
    readonly: boolean = false
  ) {
    if (!educationCategory) {
      this.openSettings("education", "Du musst zuerst deine Ausbildungen für diese Kategorie konfigurieren. Möchtest du sie in den Einstellungen hinterlegen?");
      return;
    }
    this.modalController
      .create({
        component: EducationCardComponent,
        presentingElement: !document.getElementsByClassName("modal-default")
          ? this.presentingElement
          : undefined,
        componentProps: {
          educationCategory: signal(educationCategory),
          student: signal(student),
          appointment: signal(appointment),
          readonly: signal(readonly),
        },
        canDismiss: preventSwipeDismiss === false ? true : this.canDismiss,
        cssClass:
          isPlatform("ipad") ||
          isPlatform("desktop") ||
          isPlatform("phablet") ||
          isPlatform("tablet")
            ? "fullscreen"
            : "",
      })
      .then((modal) => modal.present());
  }

  openExamDialog(lesson: AppointmentDto, student: StudentDto) {
    this.modalController
      .create({
        component: ExamDialogComponent,
        presentingElement: !document.getElementsByClassName("modal-default")
          ? this.presentingElement
          : undefined,
        componentProps: {
          lesson: signal(lesson),
          student: signal(student),
        },
        canDismiss: this.canDismiss,
      })
      .then((modal) => modal.present());
  }

  openTrackingCommentDialog(location_tracking: LocationTrackingDto) {
    this.modalController
      .create({
        component: CommentDialogComponent,
        breakpoints: [0, 0.5, 1],
        initialBreakpoint: 0.5,
        componentProps: {
          location_tracking: signal(location_tracking),
        },
      })
      .then((modal) => modal.present());
  }

  openMileageDialog(lesson: AppointmentDto) {
    this.modalController
      .create({
        component: MileageDialogComponent,
        breakpoints: [0, 0.8, 1],
        initialBreakpoint: 0.8,
        componentProps: {
          lesson: signal(lesson),
        },
      })
      .then((modal) => modal.present());
  }

  openExamListDialog(exams: AppointmentDto[], student: StudentDto) {
    this.modalController
      .create({
        component: ExamListComponent,
        componentProps: {
          exams: signal(exams),
          student: signal(student),
        },
      })
      .then((modal) => modal.present());
  }

  openReportDialog(
    report?: ReportGeneratorDto,
    studentName?: string,
    screen?: string
  ) {
    this.modalController
      .create({
        component: ReportGenerationComponent,
        componentProps: {
          report: signal(report),
          studentName: signal(studentName),
          screen: signal(screen),
        },
      })
      .then((modal) => modal.present());
  }

  openProductDialog(product?: ProductDto) {
    this.modalController
      .create({
        component: ProductFormComponent,
        componentProps: {
          product: signal(product),
        },
      })
      .then((modal) => modal.present());
  }

  openNewSubscriptionAlert(
    student: StudentDto,
    subscriptions: SubscriptionDto[]
  ) {
    let selectedSubscription;
    const inputs = subscriptions.map((subscription) => {
      return {
        label: subscription.name,
        type: "radio" as "radio",
        value: subscription.subscription_id,
        handler: () => {
          selectedSubscription = subscription;
        },
      };
    });
    this.alertController
      .create({
        header: "Neues Abo verkaufen",
        inputs: inputs,
        buttons: [
          {
            text: "Abbrechen",
            role: "cancel",
          },
          {
            text: "Verkaufen",
            handler: (data) => {
              this.createInvoice(student, selectedSubscription);
            },
          },
        ],
      })
      .then((alert) => alert.present());
  }

  openSettings(setting: string, text: string): Promise<boolean> {
    let path = setting;

    return new Promise((resolve) => {
        this.alertController
            .create({
                header: "Zusätzliche Informationen erforderlich",
                message: text,
                buttons: [
                    {
                        text: "Abbrechen",
                        role: "cancel",
                        handler: () => {
                            resolve(false); // Resolve the promise on cancel
                        },
                    },
                    {
                        text: "Zu den Einstellungen",
                        handler: () => {
                            if (this.platform.width() >= 700) {
                                path = "/home/tab4";
                            }
                            this.navController.navigateForward([path]);
                            resolve(true); // Resolve the promise after navigation
                        },
                    },
                ],
            })
            .then((alert) => alert.present());
    });
  }

  private createInvoice(student: StudentDto, subscription: SubscriptionDto) {
    const student_id = student;
    const newInvoice = {
      student_id: student_id,
      invoice_items: [
        {
          id: 0,
          prize: subscription.prize,
          initial_prize: subscription.prize,
          name: subscription.name,
          date: new Date().toISOString().slice(0, 10),
          amount: subscription.lessons,
          unit: subscription.lessons > 1 ? "Lektionen" : "Lektion",
          subscription_id: subscription.subscription_id,
        } as InvoiceItemDto,
      ],
    };
    this.openNewInvoiceDialog(newInvoice);
  }

  async canDismiss(data?: any, role?: string) {
    return role !== "gesture";
  }
}
